import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

/* ───────────────────────────────────────────────────────────────────────────
 *  Service-Worker hygiene (Phase 52/53).
 *
 *  Historical builds shipped an aggressively-caching Service Worker that
 *  precached HTML/JS/CSS. After we switched to push-only SW, those legacy
 *  workers kept serving stale builds until users cleared site data manually.
 *
 *  This block runs on every page load and does 3 things:
 *
 *   1. Wipes every CacheStorage bucket on the origin (`caches.delete`).
 *      Safe because the current SW never writes to Cache anymore.
 *
 *   2. Registers /service-worker.js eagerly (not lazily from the push-button)
 *      so Application → Service Workers in DevTools is never empty and push
 *      subscription works without a preceding click. We explicitly pass
 *      `updateViaCache: 'none'` so the browser never serves the SW script
 *      from its built-in 24-hour HTTP cache.
 *
 *   3. Calls `.update()` on the existing registration to force a fresh
 *     byte-level check of /service-worker.js against the network — this is
 *     what makes freshly-deployed SWs activate on the very next page refresh
 *     instead of within the default 24-hour revalidation window.
 *
 *  The SW itself (see public/service-worker.js) has NO fetch handlers. It
 *  only receives push + notificationclick. Network requests from the page
 *  go straight to the network — there is nothing to cache.
 * ─────────────────────────────────────────────────────────────────────── */
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  // 1. Evict any CacheStorage leftovers from older SW versions.
  if ("caches" in window) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
  }

  // 2. Register (or pick up) the push-only SW on every load so it's always
  //    available in DevTools and ready to receive push events. We avoid a
  //    strict `process.env.NODE_ENV === 'production'` guard on purpose — the
  //    build runs under Docker where NODE_ENV may or may not be forwarded,
  //    and the SW is harmless in dev too (no fetch handlers, no caching).
  //
  //    `window.addEventListener('load', …)` defers registration until after
  //    first paint so SW installation never competes with the initial render.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/", updateViaCache: "none" })
      .then((reg) => {
        // 3. Kick a fresh byte-check against the network so a just-deployed
        //    /service-worker.js takes effect on THIS page load.
        reg.update().catch(() => {});
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[sw] registration failed:", err && err.message);
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
